import { useWindowSize } from '@folklore/hooks';
import { DataProvider } from '@micromag/data';
import '@micromag/intl/locale/fr';
import Viewer from '@micromag/viewer';
import classNames from 'classnames';
import React, { useState, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'wouter';

import useStoryTrackingVariables from '../../hooks/useStoryTrackingVariables';

import { useSite } from '../../contexts/SiteContext';
import { MicromagItem, MicromagStory } from '../../types/micromag';
import Thumbnail from './Thumbnail';

import menuLogo from '../../img/laposte-white.png';
import styles from '../../styles/partials/micromag.module.css';

interface MicromagProps {
    micromag?: MicromagStory | null;
    micromags?: MicromagItem[] | null;
    viewerMenuHeader?: React.ReactNode;
    currentScreenId?: string;
    paused?: boolean;
    fullscreen?: boolean;
    autoFullscreen?: boolean;
    withoutPlaybackControls?: boolean;
    basePath?: string | null;
    withoutRouter?: boolean;
    // onStart?: () => void | null;
    onClose?: (() => void) | null;
    onEnd?: (() => void) | null;
    onScreenChange?: (...args: any[]) => void;
    onMenuChange?: (...args: any[]) => void;
    hasHome?: boolean;
    className?: string | number | symbol | any;
}

function Micromag({
    micromag = null,
    micromags = null,
    viewerMenuHeader = null,
    currentScreenId = null,
    paused = false,
    fullscreen: initialFullscreen = false,
    autoFullscreen = false,
    withoutPlaybackControls = false,
    basePath = null,
    withoutRouter = true,
    // onStart: customOnStart = null,
    onClose: customOnClose = null,
    onEnd: customOnEnd = null,
    onScreenChange = null,
    onMenuChange = null,
    hasHome = false,
    className = null,
}: MicromagProps) {
    const googleApiKey = null;
    const micromagApiBaseUrl = 'https://microm.ag/api';
    const { organisation = null } = micromag || {};
    const { branding = null } = organisation || {};
    const { isKiosk = false } = useSite();
    const { width = 0, height = 0 } = useWindowSize();
    const [landscape, setLandscape] = useState(width > height);
    const [fullscreen, setFullscreen] = useState(autoFullscreen && !landscape && initialFullscreen);

    const onClose = useCallback(() => {
        if (autoFullscreen && fullscreen) {
            setFullscreen(false);
        }
        if (customOnClose !== null) {
            customOnClose();
        }
    }, [autoFullscreen, fullscreen, setFullscreen, customOnClose]);

    // const onStart = useCallback(() => {
    //     if (autoFullscreen && !landscape) {
    //         setFullscreen(true);
    //     }
    //     if (customOnStart !== null) {
    //         customOnStart();
    //     }
    // }, [autoFullscreen, setFullscreen, landscape, customOnStart]);

    const onEnd = useCallback(() => {
        if (autoFullscreen && fullscreen) {
            setFullscreen(false);
        }
        if (customOnEnd !== null) {
            customOnEnd();
        }
    }, [autoFullscreen, fullscreen, setFullscreen, customOnEnd]);

    const onViewModeChange = useCallback(
        ({ landscape: isLandscape = false }) => {
            setLandscape(isLandscape);
            if (autoFullscreen && !isLandscape) {
                setFullscreen(true);
            }
        },
        [autoFullscreen, setLandscape, setFullscreen],
    );

    const trackingVariables = useStoryTrackingVariables(micromag);

    const finalMicromag = useMemo(
        () => ({
            title: 'Custom micromag title',
            ...micromag,
        }),
        [micromag],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.fullscreen]: fullscreen,
                    [className]: className !== null,
                },
            ])}
        >
            <DataProvider apiBaseUrl={micromagApiBaseUrl}>
                <Viewer
                    story={finalMicromag}
                    trackingVariables={trackingVariables}
                    paused={paused}
                    theme={branding}
                    locale="fr"
                    visitor={null}
                    memoryRouter={basePath === null}
                    withoutRouter={withoutRouter}
                    basePath={basePath}
                    screen={currentScreenId}
                    closeable={(autoFullscreen && fullscreen) || customOnClose !== null}
                    googleApiKey={googleApiKey}
                    backToFirstScreenTimeout={isKiosk ? 30 * 1000 : null}
                    // muted={!isKiosk}
                    onViewModeChange={onViewModeChange}
                    onScreenChange={onScreenChange}
                    onMenuChange={onMenuChange}
                    onClose={onClose}
                    onEnd={onEnd}
                    shareOptions={['linkedin', 'email']}
                    withoutPlaybackControls={withoutPlaybackControls}
                    withNavigationHint
                    withMicromagBranding
                    pathWithIndex
                    menuHeader={viewerMenuHeader}
                    menuItems={
                        !isKiosk
                            ? [
                                  <Link className={styles.link} to="/">
                                      <img
                                          src={menuLogo}
                                          alt="La Poste"
                                          className={styles.menuLogo}
                                      />
                                      Retourner à la page d’accueil
                                  </Link>,
                                  'share',
                                  'main',
                              ]
                            : ['main']
                    }
                    menuFooter={
                        micromags !== null && micromags.length > 0 && !isKiosk ? (
                            <div className={styles.micromags}>
                                <p className={styles.subtitle}>
                                    Découvrez les autres postiers de l'année&nbsp;:
                                </p>
                                <ul className={styles.list}>
                                    {micromags.map((it) => {
                                        const { id = null, slug = '' } = it || {};
                                        return (
                                            <a
                                                className={classNames([styles.micromagItem])}
                                                id={`micromag-item-${id}`}
                                                href={`/${slug}`}
                                                target="_self"
                                                key={`micromag-item-${id}`}
                                            >
                                                <Thumbnail
                                                    className={styles.thumbnail}
                                                    innerClassName={styles.video}
                                                    micromag={it}
                                                    isPoster
                                                />
                                            </a>
                                        );
                                    })}
                                </ul>
                            </div>
                        ) : null
                    }
                />
            </DataProvider>
        </div>
    );
}

export default Micromag;
